'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Link from '@lichtblick/link';
import { AccentText } from '@lichtblick/photon/atoms/accent-text/accent-text';
import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { ChevronLeftXs } from '@lichtblick/photon/icons/arrows/chevron-left-xs';
import { ChevronRightXs } from '@lichtblick/photon/icons/arrows/chevron-right-xs';
import { BarsHorizontalS } from '@lichtblick/photon/icons/objects/bars-horizontal-s';
import { PersonXs } from '@lichtblick/photon/icons/objects/person-xs';
import { SpeechBubbleXs } from '@lichtblick/photon/icons/objects/speech-bubble-xs';
import { CrossS } from '@lichtblick/photon/icons/symbols/cross-s';
import { TeaserItem } from '@lichtblick/photon/molecules/teaser-item/teaser-item';
import classNames from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import { buildUrl } from '../../helpers/links';
import { useScrollLock } from '../../hooks/use-scroll-lock';
import { AImage } from '../AImage';
import { LiLevel0 } from './li-level-0';
import { LiLevel1 } from './li-level-1';
import styles from './navigation.module.scss';
import { Search } from './search';
import { useClickHandler } from './use-click-handler';
import { useIsMobile } from './use-mobile';
import { useNavigationState } from './use-navigation-state';
export const NavigationList = ({ items })=>{
    const { activeLevel, setNavigationLevel } = useNavigationState();
    const { isMobile } = useIsMobile();
    const navClassNames = useMemo(()=>classNames(styles.nav, activeLevel >= 0 && styles['nav-active'], !isMobile && styles['nav-hidden']), [
        activeLevel,
        isMobile
    ]);
    const navRef = useRef(null);
    const scrollWrapperRef = useRef(null);
    const closeNavigation = useCallback(()=>{
        setNavigationLevel(-1);
    }, [
        setNavigationLevel
    ]);
    useScrollLock(isMobile && activeLevel >= 0);
    useClickHandler({
        onPress: closeNavigation,
        ref: navRef
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("button", {
                "aria-expanded": activeLevel >= 0,
                "aria-label": "Menü öffnen",
                className: styles['burger-menu-button'],
                onClick: ()=>setNavigationLevel(0),
                type: "button",
                children: /*#__PURE__*/ _jsx(BarsHorizontalS, {
                    "aria-hidden": true
                })
            }),
            /*#__PURE__*/ _jsxs("nav", {
                "aria-label": "Hauptnavigation",
                className: navClassNames,
                ref: navRef,
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        "aria-label": "Menü schließen",
                        className: styles['nav-close-button'],
                        onClick: closeNavigation,
                        type: "button",
                        children: /*#__PURE__*/ _jsx(CrossS, {
                            "aria-hidden": true
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles['scroll-wrapper'],
                        ref: scrollWrapperRef,
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: classNames(styles['nav-level-0'], activeLevel === 0 && styles['nav-level-0-active']),
                            children: [
                                /*#__PURE__*/ _jsx(Search, {
                                    closeNavigation: closeNavigation
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: styles['nav-level-0-list'],
                                    children: items.map((item, index)=>// Navigation level 0 list item
                                        /*#__PURE__*/ _jsxs(LiLevel0, {
                                            navigationGroups: item.navigationGroups,
                                            children: [
                                                /*#__PURE__*/ _jsxs("button", {
                                                    type: "button",
                                                    "aria-expanded": activeLevel === index + 1,
                                                    className: styles['nav-level-0-button'],
                                                    onClick: ()=>{
                                                        setNavigationLevel(index + 1);
                                                        scrollWrapperRef.current?.scrollTo({
                                                            top: 0
                                                        });
                                                    },
                                                    children: [
                                                        item.title,
                                                        " ",
                                                        /*#__PURE__*/ _jsx(ChevronRightXs, {})
                                                    ]
                                                }),
                                                item.navigationGroups?.length && /*#__PURE__*/ _jsxs("div", {
                                                    className: classNames(styles['nav-level-1'], activeLevel === index + 1 && styles['nav-level-1-active']),
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: styles['nav-level-1-header'],
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("button", {
                                                                    type: "button",
                                                                    className: styles['nav-level-1-back-button'],
                                                                    onClick: ()=>setNavigationLevel(0),
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(ChevronLeftXs, {}),
                                                                        /*#__PURE__*/ _jsx(AccentText, {
                                                                            children: item.title
                                                                        })
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx(Text, {
                                                                    bold: true,
                                                                    className: styles['nav-level-1-title'],
                                                                    size: "l",
                                                                    children: item.title
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("ul", {
                                                            className: classNames(styles['nav-groups-list'], styles[`nav-groups-list-columns-${item.navigationGroupColumns}`]),
                                                            children: [
                                                                item.navigationGroups.map((group)=>/*#__PURE__*/ _jsxs("li", {
                                                                        className: styles['nav-groups-list-item'],
                                                                        children: [
                                                                            group.title && /*#__PURE__*/ _jsx(Text, {
                                                                                bold: true,
                                                                                className: styles['nav-groups-list-title'],
                                                                                size: "l",
                                                                                children: /*#__PURE__*/ _jsx("span", {
                                                                                    children: group.title
                                                                                })
                                                                            }),
                                                                            /*#__PURE__*/ _jsx("ul", {
                                                                                children: group.items.map((item)=>// Navigation group list item
                                                                                    /*#__PURE__*/ _jsx(LiLevel1, {
                                                                                        navigationItem: item,
                                                                                        children: /*#__PURE__*/ _jsx(Link, {
                                                                                            className: styles['nav-group-list-link'],
                                                                                            href: buildUrl(item.link) ?? '',
                                                                                            onClick: closeNavigation,
                                                                                            children: item.title
                                                                                        })
                                                                                    }, item._uid))
                                                                            })
                                                                        ]
                                                                    }, group._uid)),
                                                                Boolean(item.additionalNavigationItems?.length) && /*#__PURE__*/ _jsxs("li", {
                                                                    className: styles['nav-groups-list-item'],
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx(Text, {
                                                                            bold: true,
                                                                            className: classNames(styles['nav-groups-list-title'], styles['nav-groups-list-title-additional-items']),
                                                                            size: "l",
                                                                            children: /*#__PURE__*/ _jsx("span", {
                                                                                children: "Weitere Themen"
                                                                            })
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("ul", {
                                                                            children: item.additionalNavigationItems?.map((item)=>// Additional navigation item
                                                                                /*#__PURE__*/ _jsx(LiLevel1, {
                                                                                    navigationItem: item,
                                                                                    children: /*#__PURE__*/ _jsx(Link, {
                                                                                        className: styles['nav-group-list-link'],
                                                                                        href: buildUrl(item.link) ?? '',
                                                                                        onClick: closeNavigation,
                                                                                        children: item.title
                                                                                    })
                                                                                }, item._uid))
                                                                        })
                                                                    ]
                                                                }, item._uid)
                                                            ]
                                                        }),
                                                        item.image?.filename && /*#__PURE__*/ _jsx("div", {
                                                            className: styles.image,
                                                            children: /*#__PURE__*/ _jsx(AImage, {
                                                                imageMobile: item.image,
                                                                altText: item.image.title ?? '',
                                                                sizes: "(max-width: 300px) 100vw, 300px"
                                                            })
                                                        }),
                                                        !item.image?.filename && Boolean(item?.teaserItems?.length) && /*#__PURE__*/ _jsx("div", {
                                                            className: classNames(styles['teaser-list'], styles[`teaser-list-columns-${item.teaserItemColumns}`]),
                                                            children: item.teaserItems?.map((item)=>/*#__PURE__*/ _jsx(TeaserItem, {
                                                                    className: styles['teaser-list-item'],
                                                                    link: {
                                                                        href: buildUrl(item.link),
                                                                        title: item.title,
                                                                        onClick: closeNavigation
                                                                    },
                                                                    media: /*#__PURE__*/ _jsx(AImage, {
                                                                        imageMobile: item.image,
                                                                        altText: item.image.title ?? '',
                                                                        className: styles['teaser-list-item-image'],
                                                                        sizes: "184px"
                                                                    }),
                                                                    text: item.title,
                                                                    textOutside: true
                                                                }, item._uid))
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, item._uid))
                                }),
                                /*#__PURE__*/ _jsx(BoxCTA, {
                                    as: "a",
                                    className: styles['nav-level-0-meta-button'],
                                    href: "/konto/",
                                    leftSlot: /*#__PURE__*/ _jsx(PersonXs, {}),
                                    variant: "neutral-filled",
                                    children: "Mein Konto"
                                }),
                                /*#__PURE__*/ _jsx(BoxCTA, {
                                    as: "a",
                                    className: styles['nav-level-0-meta-button'],
                                    href: "/kontakt/",
                                    leftSlot: /*#__PURE__*/ _jsx(SpeechBubbleXs, {}),
                                    variant: "neutral-outline",
                                    onClick: closeNavigation,
                                    children: "Kontakt"
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
};

//# sourceMappingURL=navigation-list.js.map